import {Lang} from 'yougile-lang'
import {logger, Util, UUID} from 'yougile-util'
import {
  DataObjectId,
  Hub,
  HubLike,
  InviteCompanyReq,
  InviteCompanyResp,
  PermissionCustomRole,
  PermissionRoleKey,
  rootNodeId,
  SandboxTree,
  User
} from 'yougile-model'
import {TrackingEvent} from '@common/tracking-events'
import {Backend} from '../../common/backend'
import {Tracking} from '../../common/tracking'
import {Accounts} from '../client/accounts'
import {Connector} from '../client/connector'
import {DepartmentsClient} from '../client/departments-client'
import {PaymentClient} from '../client/payment-client'
import {PermissionsChecker} from '../client/permissions-checker'
import {SharedBoardsHelper} from '../client/shared-boards-helper'
import {Sync} from '../client/sync'
import {MessageBox} from '../components/message-box'
import {globals} from './globals-base'
import {Project} from './project'
import {SystemEntities} from './system-entities'

/**
 * This file controls user privileges.
 *
 * Now it supports adding and removing users from projects.
 * A user sees only the projects where this user is added.
 * If there are no users in project then the oldest one is added automatically.
 */
export class UserAccess {
  public static fixDeletedUsers(box: SandboxTree): void {
    if (!box) {
      return
    }
    if (!PermissionsChecker.isAdmin({box})) {
      return
    }
    if (!Connector.isReady()) {
      return
    }
    const cmp = PaymentClient.getUsers(box.companyId)
    logger.log('Checking user list…')
    Object.keys(cmp).forEach((id: DataObjectId) => {
      const u = box.get(id)
      if (!u || !box.isUserInCompany(id)) {
        Backend.report(
          'Deleting incorrect user',
          id,
          box.companyId,
          String(!!u)
        )
        logger.log('Deleting incorrect user', id, box.companyId)
        Connector.request('delete-user', {
          targetCompanyId: box.companyId,
          targetUserId: id
        })
      }
    })
  }

  public static canChangeUserInProject(project: HubLike | undefined): boolean {
    if (!project) {
      return false
    }
    const box = Sync.get(project)
    const prHub = box.get(project.id) as Hub
    return (
      prHub &&
      (PermissionsChecker.isAdmin() ||
        UserAccess.getRoleInProject(globals.currentUserId, prHub) === 'admin')
    )
  }

  public static canInviteUser(
    companyId: DataObjectId,
    checkDisableInviteUsers = true
  ): boolean {
    if (globals.disableInviteUsers && checkDisableInviteUsers) {
      return false
    }
    const box = Sync.get({companyId})
    if (!box) {
      return false
    }
    if (PermissionsChecker.isAdmin()) {
      return true
    }
    return (
      DepartmentsClient.isManager(box, globals.currentUserId) ||
      Project.listProjects(companyId, 'all').some(
        UserAccess.canChangeUserInProject
      )
    )
  }

  public static removeUserFromCompany(
    userId: DataObjectId,
    box: SandboxTree,
    force?: boolean
  ): void {
    if (Sync.profileId === box.companyId) {
      return
    }
    if (box.isUserInCompany(userId) || force) {
      box.disconnect(userId, box.companyId)
      Connector.request('delete-user', {
        targetCompanyId: box.companyId,
        targetUserId: userId
      })
        .then(() => box.users.delete(userId))
        .catch(res => MessageBox.show(res.error, 'danger'))
    } else {
      logger.error('User is not in company')
    }
  }

  /**
   * Creates user that is not synchronized with server, just user in the
   * sandbox. To make this user real user should be invited (“.inviteUser”).
   */
  public static createUserInCompany(
    userId: DataObjectId,
    email: string,
    box: SandboxTree
  ): User {
    let newUser = box.get(userId) as User
    if (!newUser) {
      newUser = new User(box.companyId, userId, email)
      box.add(newUser, rootNodeId)
    }
    box.connect(newUser.id, box.companyId)
    return newUser
  }

  public static listProjectsForUser(
    box: SandboxTree,
    user: User,
    showOnlyUserProjects?: boolean
  ): Hub[] {
    const proj = Project.listProjects(
      box.companyId,
      'not-archive',
      showOnlyUserProjects
    )
    return proj.filter(p => UserAccess.getRoleInProject(user.id, p))
  }

  public static listUsersInProject(project: {
    companyId: DataObjectId
    id: DataObjectId
  }): User[] {
    if (!project) {
      return []
    }
    const res: User[] = []
    const box = Sync.get(project)
    if (!box) {
      return []
    }
    const prHub = box.get(project.id) as Hub
    box.getAllUsers(false).forEach((user: User) => {
      if (
        box.isUserInCompany(user.id) &&
        !!UserAccess.getRoleInProject(user.id, prHub)
      ) {
        res.push(user)
      }
    })
    return res
  }

  public static getRoleTitle(
    roleKey: PermissionRoleKey,
    customRoles?: PermissionCustomRole[]
  ) {
    return roleKey === 'admin'
      ? Lang.msg.permission.adminRole
      : roleKey === 'worker'
        ? Lang.msg.permission.workerRole
        : roleKey === 'observer'
          ? Lang.msg.permission.observerRole
          : customRoles?.find(r => r.key === roleKey)?.name ?? '–'
  }

  public static getRoleInProject(
    userId: DataObjectId,
    project: Hub | undefined
  ): PermissionRoleKey | undefined {
    if (!project || !userId) {
      return undefined
    }
    const sharedRole = SharedBoardsHelper.getSharedRoleKey(project.id)
    if (sharedRole) {
      return sharedRole
    }
    if (project.data.userPermissions) {
      return project.data.userPermissions[userId]
    } else {
      // Deprecated old way of checking that user is in project
      // TODO: remove this
      const box = Sync.get(project)
      const accessTag = SystemEntities.get(project.companyId).accessTagId
      const connected =
        box.isConnected(accessTag, project.id) &&
        box.getModifiers(accessTag, project.id).includes(userId)
      return connected ? 'worker' : undefined
    }
  }

  public static setRoleInProject(
    userId: DataObjectId,
    project: Hub,
    role: PermissionRoleKey
  ): void {
    if (!UserAccess.canChangeUserInProject(project)) {
      return
    }
    project.data.userPermissions = {
      ...project.data.userPermissions,
      [userId]: role
    }
    project.commit()
  }

  public static removeRoleInProject(userId: DataObjectId, project: Hub): void {
    if (!UserAccess.canChangeUserInProject(project)) {
      return
    }
    if (project.data.userPermissions) {
      delete project.data.userPermissions[userId]
      project.commit()
    }
    const box = Sync.get(project)
    const accessTag = SystemEntities.get(project.companyId).accessTagId
    if (box.isConnected(accessTag, project.id)) {
      box.removeModifiers(accessTag, project.id, [userId])
    }
  }

  public static invite(
    email: string,
    companyId: DataObjectId,
    projectInvite:
      | {projectId: DataObjectId; role: PermissionRoleKey}[]
      | undefined,
    cb?: (user: User | undefined, error: string) => void
  ): Promise<{error?: string}> {
    return new Promise(resolve => {
      if (!Util.isEmail(email)) {
        resolve({error: Lang.msg.emailIncorrect})
        cb?.(undefined, Lang.msg.emailIncorrect)
        return
      }
      const profile = Sync.getProfile()
      UserAccess.inviteToCompany(
        email,
        companyId,
        projectInvite,
        (uid: DataObjectId, error: string) => {
          if (uid) {
            if (!profile.has(uid)) {
              UserAccess.createUserInCompany(uid, email, profile)
            }
            Accounts.addUsers([uid])
            if (!profile.isUserInCompany(uid)) {
              profile.restoreUserInProfile(uid)
            }
            resolve({})
            cb?.(profile.get(uid) as User, '')
            if (error) {
              logger.warn(error)
            }
          } else {
            resolve({error})
            cb?.(undefined, error)
          }
        }
      )
    })
  }

  private static inviteToCompany(
    email: string,
    companyId: DataObjectId,
    projectInvite:
      | {projectId: DataObjectId; role: PermissionRoleKey}[]
      | undefined,
    cb: (userId: DataObjectId, err?: string) => void
  ): void {
    const box = Sync.get({companyId})
    const userKey = Backend.loadLocal('user_key') as UUID
    const invReq: InviteCompanyReq = {
      userId: globals.currentUserId,
      companyId: box.companyId,
      key: userKey,
      email
    }
    Tracking.send(TrackingEvent.companyInvite)
    const invUrl = '/data/invite-by-email'
    Backend.postJSON<InviteCompanyResp>(invUrl, invReq)
      .then(res => {
        Tracking.send(TrackingEvent.companyInviteSuccess)
        const invitedId = res.invitedId
        if (!box.has(invitedId)) {
          box.users.set(invitedId, {isAdmin: false})
        }
        UserAccess.createUserInCompany(invitedId, email, box)
        if (projectInvite) {
          for (const proj of projectInvite) {
            const pr = box.get(proj.projectId) as Hub
            if (!UserAccess.getRoleInProject(invitedId, pr)) {
              UserAccess.setRoleInProject(invitedId, pr, proj.role)
            }
          }
        }
        cb(invitedId)
      })
      .catch(error => {
        Tracking.send(TrackingEvent.companyInviteError)
        cb(undefined, error)
      })
  }
}
